/**
 * @file        src/App.jsx
 * @description Admin console router. Bootstraps auth on mount (silent refresh
 *              via httpOnly cookie). Every menu entry in src/lib/nav.js gets a
 *              live route — bespoke pages where they exist, otherwise <Stub />.
 * @author      Kinetika Labs
 * @created     2026-06-22
 * @changelog
 *   - 2026-10-10 Claude — Amendment Markup screens (B2C + Corporate, flight and
 *                          hotel; admin migration 078).
 *   - 2026-10-09 Claude — SEO Management: the five /seo/* screens come from
 *                          flatPaths()/BESPOKE; the per-page editor
 *                          (seo/pages/:pageKey) is declared here like the other
 *                          :id/edit routes, since it is not a nav path.
 *   - 2026-10-09 Claude — /{flight,hotel,insurance}/bookings/:id/details: full-page Booking Details.
 *   - 2026-09-22 Claude — Insurance routes: the two Insurance List screens come
 *                          from flatPaths()/BESPOKE, and the policy tax invoice
 *                          is declared explicitly here like the flight/hotel
 *                          documents (a :id route is not a nav path).
 *  - 2026-09-24 Claude — Added /account/action-center (the console's open-work
 *                         queue, derived from eleven existing queues) and
 *                         /account/activity-log (the audit trail that has been
 *                         written since 2026-07-27 with no reader but the
 *                         wallet-filtered Recharge Log).
 *  - 2026-09-22 Claude — Added the Payment Reconciliation route
 *                         (/account/payment-reconciliation).
 *  - 2026-09-22 Claude — Added the Abandoned Payments route
 *                         (/account/abandoned-payments).
 *   - 2026-09-15 Claude — Removed the Home Slider Image routes (SliderList /
 *                          AddSlider imports, both PAGES entries and both
 *                          add/edit sub-routes). The screen wrote rows no
 *                          storefront could read — /api/home-slider was fully
 *                          module-gated, so there was no public read path.
 *   - 2026-06-22 Claude — Initial.
 *   - 2026-07-02 Claude — Initial (B2C Hotel Management): hotel routes.
 *   - 2026-07-04 Claude — Account: Balance Sheet page. API Management:
 *                          Supplier List + Log Menu pages.
 *   - 2026-07-04 Claude — Offer Management: Offer List + Add/Edit Offer pages.
 *   - 2026-07-27 Claude — Platform routes (Tenant Management + Platform Admin
 *                          Users), gated to role='super_admin' via a nested
 *                          ProtectedRoute layer around just those 4 routes.
 *   - 2026-07-27 Claude — CorporateAccounts replaces AgentAccounts; added
 *                          Corporate Flight/Hotel booking lists and the
 *                          Wallet Recharge Log page.
 *   - 2026-08-03 Claude — Role split completed: every tenant-operations route
 *                          is now wrapped in ProtectedRoute role="tenant_admin"
 *                          (mirror of the /platform super_admin wrapper), and
 *                          "/" is role-aware — a super_admin lands on Tenant
 *                          Management, never the (tenant-scoped) Dashboard.
 *   - 2026-08-04 Claude — FAQ Management routes (list / new / edit, CR A2).
 *   - 2026-08-04 Claude — <BrandingHead />: tenant name + favicon on the
 *                          browser tab (was hardcoded NeonHolidays for every
 *                          tenant in index.html).
 *   - 2026-08-04 Claude — Query Management → Query List now a real page
 *                          (EnquiryList), replacing the Stub (CR A5).
 *   - 2026-08-04 Claude — Account Management → Credit Dues now a real page
 *                          (CreditDues): the B2B post-pay credit line's ledger
 *                          + settlement (Corporate Accounts configures the
 *                          line itself).
 *   - 2026-08-05 Claude — Fixed: removing "Add New Customer" from nav.js
 *                          (sidebar-only change, per that commit's own message)
 *                          had also deleted its route, since routes here are
 *                          generated FROM nav.js's flatPaths(), not declared
 *                          separately — /customers/new fell through to the
 *                          catch-all and bounced to Dashboard. Declared it
 *                          explicitly alongside customers/:id/edit, matching
 *                          every other sidebar-hidden-but-reachable route.
 *   - 2026-08-05 Claude — Account Management → Email Settings now a real page
 *                          (EmailSettings): per-brand SMTP delivery config.
 *   - 2026-08-05 Claude — Account Management → Communication Log now a real
 *                          page (CommunicationLog): read-only viewer over
 *                          every outbound email attempt from either backend.
 *   - 2026-08-07 Claude — Removed Package Setting, Sub Category List,
 *                          Inclusion List, and Exclusion List routes/imports
 *                          — unused scaffolding, see nav.js changelog.
 *   - 2026-08-10 Claude — Corporate Flight/Hotel gained 6 routes each (Discount
 *                          List, Convenience Fee, Coupon List, Used Coupon
 *                          List, Cancellation List, Search Log/History) — thin
 *                          audience=b2b wrappers around the existing B2C pages,
 *                          same pattern as the existing Corporate Booking List.
 *   - 2026-08-10 Claude — Account Management gained Corporate Requests
 *                          (CorporateRequests): the approval queue for the
 *                          storefront's "Register your company" form
 *                          (corporate_registration_requests, migration 049,
 *                          /api/corporate-requests) — approve/reject with an
 *                          emailed decision.
 *   - 2026-08-10 Claude — Tenant Management restructured: 4 new pages
 *                          (TenantBrandsPage/TenantGatewaysPage/
 *                          TenantSuppliersPage/TenantBrandingPage) for the
 *                          onboarding panels that used to be stacked on the
 *                          tenant edit page. "platform/tenants/new" is
 *                          declared explicitly (no longer in nav.js's
 *                          flatPaths — see its changelog) alongside the
 *                          existing ":id/edit" route.
 *   - 2026-09-15 Claude — Removed the customers/new and customers/:id/edit
 *                          routes with AddCustomer.jsx. Customers register
 *                          themselves on the storefront; an admin-authored row
 *                          was an account nobody could sign into, and an
 *                          admin-edited email moved the login out from under a
 *                          real customer. The Customer List is now read-only
 *                          apart from Enable/Disable.
 */
import { useEffect } from 'react';
import { Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { useAuth } from './lib/auth.store';
import { flatPaths } from './lib/nav';

import AdminLayout from './layout/AdminLayout';
import ProtectedRoute from './layout/ProtectedRoute';
import ToastHost from './components/ToastHost';
import ConfirmHost from './components/ConfirmHost';
import BrandingHead from './components/BrandingHead';

import Login from './pages/Login';
import ForgotPassword from './pages/ForgotPassword';
import Dashboard from './pages/Dashboard';
import CorporateDashboard from './pages/CorporateDashboard';
import ActionCenter from './pages/account/ActionCenter';
import ActivityLog from './pages/account/ActivityLog';
import Stub from './pages/Stub';
import AirportList from './pages/airports/AirportList';
import AddAirport from './pages/airports/AddAirport';
import Transactions from './pages/account/Transactions';
import BalanceSheet from './pages/account/BalanceSheet';
import CorporateAccounts from './pages/account/CorporateAccounts';
import CorporateRequests from './pages/account/CorporateRequests';
import OutstandingDues from './pages/wallet/OutstandingDues';
import AbandonedPayments from './pages/account/AbandonedPayments';
import PaymentReconciliation from './pages/account/PaymentReconciliation';
import CompanyWallets from './pages/account/WalletManagement';
import Reports from './pages/account/Reports';
import CorporateReports from './pages/account/CorporateReports';
import EmailSettings from './pages/emailSettings/EmailSettings';
import CommunicationLog from './pages/communicationLog/CommunicationLog';
import CategoryList from './pages/package/CategoryList';
import AddCategory from './pages/package/AddCategory';
import PackageList from './pages/package/PackageList';
import AddPackage from './pages/package/AddPackage';
import HolidayList from './pages/package/HolidayList';
import AddHoliday from './pages/package/AddHoliday';
import BlogCategoryList from './pages/blog/BlogCategoryList';
import AddBlogCategory from './pages/blog/AddBlogCategory';
import BlogList from './pages/blog/BlogList';
import AddBlog from './pages/blog/AddBlog';
import FlightBookingList from './pages/flight/BookingList';
import FlightInvoiceDoc from './pages/flight/InvoiceDoc';
import BookingDetailsPage from './pages/BookingDetailsPage';
import FlightTicketDoc from './pages/flight/TicketDoc';
import HotelInvoiceDoc from './pages/hotel/InvoiceDoc';
import HotelVoucherDoc from './pages/hotel/VoucherDoc';
import FlightSearchHistory from './pages/flight/SearchHistory';
import FlightSearchAnalytics from './pages/flight/SearchAnalytics';
import FlightUsedCoupons from './pages/flight/UsedCoupons';
import FlightCancellations from './pages/flight/Cancellations';
import FlightCouponList from './pages/flight/CouponList';
import FlightDiscountList from './pages/flight/DiscountList';
import FlightConvenienceFee from './pages/flight/ConvenienceFee';
import FlightAmendmentMarkup from './pages/flight/AmendmentMarkup';
import CorporateFlightAmendmentMarkup from './pages/flight/CorporateAmendmentMarkup';
import HotelAmendmentMarkup from './pages/hotel/AmendmentMarkup';
import CorporateHotelAmendmentMarkup from './pages/hotel/CorporateAmendmentMarkup';
import FlightMarkupList from './pages/flight/MarkupList';
import B2BFlightMarkupList from './pages/flight/B2BMarkupList';
import B2BHotelMarkupList from './pages/hotel/B2BMarkupList';
import CorporateFlightBookingList from './pages/flight/CorporateBookingList';
import CorporateHotelBookingList from './pages/hotel/CorporateBookingList';
import CorporateFlightDiscountList from './pages/flight/CorporateDiscountList';
import CorporateFlightConvenienceFee from './pages/flight/CorporateConvenienceFee';
import CorporateFlightCouponList from './pages/flight/CorporateCouponList';
import CorporateFlightCancellationList from './pages/flight/CorporateCancellationList';
import CorporateFlightSearchHistory from './pages/flight/CorporateSearchHistory';
import CorporateFlightSearchAnalytics from './pages/flight/CorporateSearchAnalytics';
import CorporateFlightUsedCoupons from './pages/flight/CorporateUsedCoupons';
import InsuranceBookingList from './pages/insurance/BookingList';
import CorporateInsuranceBookingList from './pages/insurance/CorporateBookingList';
import InsuranceInvoiceDoc from './pages/insurance/InvoiceDoc';
import InsurancePolicyRequests from './pages/insurance/PolicyRequests';
import CorporateInsurancePolicyRequests from './pages/insurance/CorporatePolicyRequests';
import CorporateHotelDiscountList from './pages/hotel/CorporateDiscountList';
import CorporateHotelConvenienceFee from './pages/hotel/CorporateConvenienceFee';
import CorporateHotelCouponList from './pages/hotel/CorporateCouponList';
import CorporateHotelCancellationList from './pages/hotel/CorporateCancellationList';
import CorporateHotelSearchLog from './pages/hotel/CorporateSearchLog';
import CorporateHotelUsedCouponList from './pages/hotel/CorporateUsedCouponList';
import HotelBookingList from './pages/hotel/BookingList';
import HotelMarkupList from './pages/hotel/MarkupList';
import HotelAddMarkup from './pages/hotel/AddMarkup';
import HotelDiscountList from './pages/hotel/DiscountList';
import HotelAddDiscount from './pages/hotel/AddDiscount';
import HotelCouponList from './pages/hotel/CouponList';
import HotelUsedCouponList from './pages/hotel/UsedCouponList';
import HotelConvenienceFee from './pages/hotel/ConvenienceFee';
import HotelCancellationList from './pages/hotel/CancellationList';
import HotelSearchLog from './pages/hotel/SearchLog';
import HotelSearchLogDetails from './pages/hotel/SearchLogDetails';
import CustomerList from './pages/customer/CustomerList';
import StaffList from './pages/staff/StaffList';
import AddStaff from './pages/staff/AddStaff';
import SupplierList from './pages/api/SupplierList';
import LogMenu from './pages/api/LogMenu';
import OfferList from './pages/offer/OfferList';
import AddOffer from './pages/offer/AddOffer';
import GatewayList from './pages/payment/GatewayList';
import TaxManagement from './pages/payment/TaxManagement';
import InvoiceSeriesSettings from './pages/payment/InvoiceSeriesSettings';
import TestimonialList from './pages/testimonial/TestimonialList';
import AddTestimonial from './pages/testimonial/AddTestimonial';
import FaqList from './pages/faq/FaqList';
import AddFaq from './pages/faq/AddFaq';
import EnquiryList from './pages/enquiry/EnquiryList';
import GroupRequestList from './pages/enquiry/GroupRequestList';
import SiteSetting from './pages/site/SiteSetting';
import SocialLinks from './pages/site/SocialLinks';
import SeoPages from './pages/seo/SeoPages';
import SeoPageEditor from './pages/seo/SeoPageEditor';
import SeoSettings from './pages/seo/SeoSettings';
import SeoRedirects from './pages/seo/SeoRedirects';
import SeoRobots from './pages/seo/SeoRobots';
import SeoHealth from './pages/seo/SeoHealth';
import WalletUsers from './pages/wallet/WalletUsers';
import WalletDetail from './pages/wallet/WalletDetail';
import WalletSettings from './pages/wallet/WalletSettings';
import WalletAuditLog from './pages/wallet/WalletAuditLog';
import TenantList from './pages/platform/TenantList';
import AddTenant from './pages/platform/AddTenant';
import TenantBrandsPage from './pages/platform/TenantBrandsPage';
import TenantGatewaysPage from './pages/platform/TenantGatewaysPage';
import TenantSuppliersPage from './pages/platform/TenantSuppliersPage';
import TenantBrandingPage from './pages/platform/TenantBrandingPage';
import TenantCompanyPage from './pages/platform/TenantCompanyPage';
import PlatformAdminUserList from './pages/platform/PlatformAdminUserList';
import AddPlatformAdminUser from './pages/platform/AddPlatformAdminUser';
import SupplierLogs from './pages/platform/SupplierLogs';

// Bespoke pages by route — anything else falls through to <Stub />.
const BESPOKE = {
  '/':                       Dashboard,
  // Audience-separated twins of "/" and /account/reports — see the file headers
  // on Dashboard.jsx / Reports.jsx: the audience is the SCREEN, not a filter.
  '/corporate-dashboard':    CorporateDashboard,
  '/airports':               AirportList,
  '/airports/new':           AddAirport,
  '/account/transactions':   Transactions,
  '/account/wallets':        WalletUsers,
  '/account/company-wallets': CompanyWallets,
  '/account/reports':        Reports,
  '/account/corporate-reports': CorporateReports,
  '/account/wallet-settings': WalletSettings,
  '/account/wallet-audit-log': WalletAuditLog,
  '/account/balance':        BalanceSheet,
  '/account/corporate-accounts': CorporateAccounts,
  '/account/corporate-requests': CorporateRequests,
  '/account/credit-dues':     OutstandingDues,
  '/account/abandoned-payments': AbandonedPayments,
  '/account/payment-reconciliation': PaymentReconciliation,
  // Whole-business on purpose, like Transaction Log and the two screens above
  // it — four of the twelve queues belong to no audience at all (§9d/§16), so
  // there is no Corporate twin of either of these.
  '/account/action-center':  ActionCenter,
  '/account/activity-log':   ActivityLog,
  '/account/email-settings':  EmailSettings,
  '/account/communication-log': CommunicationLog,
  '/package/categories':     CategoryList,
  '/package/categories/new': AddCategory,
  '/package/list':           PackageList,
  '/package/new':            AddPackage,
  '/package/holiday':        HolidayList,
  '/blog/categories':           BlogCategoryList,
  '/blog/categories/new':       AddBlogCategory,
  '/blog/posts':                BlogList,
  '/blog/posts/new':            AddBlog,
  '/flight/bookings':           FlightBookingList,
  '/flight/discounts':          FlightDiscountList,
  '/flight/discounts/new':      FlightDiscountList,
  '/flight/markups':            FlightMarkupList,
  '/flight/markups/new':        FlightMarkupList,
  '/flight/b2b-markups':        B2BFlightMarkupList,
  '/hotel/b2b-markups':         B2BHotelMarkupList,
  '/flight/corporate-bookings': CorporateFlightBookingList,
  '/insurance/bookings':            InsuranceBookingList,
  '/insurance/corporate-bookings':  CorporateInsuranceBookingList,
  '/insurance/policy-requests':           InsurancePolicyRequests,
  '/insurance/corporate-policy-requests': CorporateInsurancePolicyRequests,
  '/hotel/corporate-bookings':  CorporateHotelBookingList,
  '/flight/corporate-discounts':      CorporateFlightDiscountList,
  '/flight/corporate-convenience':    CorporateFlightConvenienceFee,
  '/flight/corporate-coupons':        CorporateFlightCouponList,
  '/flight/corporate-coupons/used':   CorporateFlightUsedCoupons,
  '/flight/corporate-cancellations':  CorporateFlightCancellationList,
  '/flight/corporate-search-history': CorporateFlightSearchHistory,
  '/flight/corporate-search-analytics': CorporateFlightSearchAnalytics,
  '/hotel/corporate-discounts':       CorporateHotelDiscountList,
  '/hotel/corporate-convenience':     CorporateHotelConvenienceFee,
  '/hotel/corporate-coupons':         CorporateHotelCouponList,
  '/hotel/corporate-coupons/used':    CorporateHotelUsedCouponList,
  '/hotel/corporate-cancellations':   CorporateHotelCancellationList,
  '/hotel/corporate-search-log':      CorporateHotelSearchLog,
  '/flight/coupons':            FlightCouponList,
  '/flight/coupons/used':       FlightUsedCoupons,
  '/flight/convenience':        FlightConvenienceFee,
  '/flight/amendment-markups':            FlightAmendmentMarkup,
  '/flight/corporate-amendment-markups':  CorporateFlightAmendmentMarkup,
  '/hotel/amendment-markups':             HotelAmendmentMarkup,
  '/hotel/corporate-amendment-markups':   CorporateHotelAmendmentMarkup,
  '/flight/cancellations':      FlightCancellations,
  '/flight/search-history':     FlightSearchHistory,
  '/flight/search-analytics':   FlightSearchAnalytics,
  '/hotel/bookings':            HotelBookingList,
  '/hotel/markups':             HotelMarkupList,
  '/hotel/markups/new':         HotelAddMarkup,
  '/hotel/discounts':           HotelDiscountList,
  '/hotel/discounts/new':       HotelAddDiscount,
  '/hotel/coupons':             HotelCouponList,
  '/hotel/coupons/used':        HotelUsedCouponList,
  '/hotel/convenience':         HotelConvenienceFee,
  '/hotel/cancellations':       HotelCancellationList,
  '/hotel/search-log':          HotelSearchLog,
  '/customers':                 CustomerList,
  '/api-mgmt/suppliers':        SupplierList,
  '/api-mgmt/logs':             LogMenu,
  '/staff':                     StaffList,
  '/staff/new':                 AddStaff,
  '/offers':                    OfferList,
  '/offers/new':                AddOffer,
  '/payments/gateways':         GatewayList,
  '/payments/tax':              TaxManagement,
  '/payments/invoice-series':   InvoiceSeriesSettings,
  '/testimonials':              TestimonialList,
  '/testimonials/new':          AddTestimonial,
  '/faqs':                      FaqList,
  '/faqs/new':                  AddFaq,
  '/queries':                   EnquiryList,
  '/queries/group-requests':    GroupRequestList,
  '/site/settings':             SiteSetting,
  '/site/social':               SocialLinks,
  '/seo/pages':                 SeoPages,
  '/seo/settings':              SeoSettings,
  '/seo/redirects':             SeoRedirects,
  '/seo/robots':                SeoRobots,
  '/seo/audit':                 SeoHealth,
  '/platform/tenants':          TenantList,
  '/platform/tenants/brands':   TenantBrandsPage,
  '/platform/tenants/gateways': TenantGatewaysPage,
  '/platform/tenants/suppliers': TenantSuppliersPage,
  '/platform/tenants/branding': TenantBrandingPage,
  '/platform/tenants/company':  TenantCompanyPage,
  '/platform/admin-users':      PlatformAdminUserList,
  '/platform/admin-users/new':  AddPlatformAdminUser,
  '/platform/supplier-logs':    SupplierLogs,
};

const isPlatformPath = (path) => path.startsWith('/platform');

// "/" is the only path both roles can land on (login redirect, role-mismatch
// bounce, 404 catch-all). A super_admin's home is Tenant Management — the
// Dashboard is tenant-scoped and its APIs 403 on the platform host.
function HomeRoute() {
  const role = useAuth((s) => s.user?.role);
  if (role === 'super_admin') return <Navigate to="/platform/tenants" replace />;
  return <Dashboard />;
}

export default function App() {
  const bootstrap = useAuth((s) => s.bootstrap);
  useEffect(() => { bootstrap(); }, [bootstrap]);

  return (
    <>
      {/* Outside <Routes> on purpose: the login/forgot-password screens need the
          tenant favicon + title too, not just the authed shell. */}
      <BrandingHead />
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />

        <Route
          element={
            <ProtectedRoute>
              <AdminLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<HomeRoute />} />

          {/* Tenant-operations routes — tenant_admin AND staff. A super_admin who
              navigates here bounces to "/" (which forwards them to Tenant
              Management); the real boundary is the backend's
              authorize()/requireModule() + the tenant pool swap. Which module a
              staff account can actually use inside here is enforced by the
              backend and hidden by Sidebar.jsx, not by this wrapper. */}
          <Route element={<ProtectedRoute role={['tenant_admin', 'staff']}><Outlet /></ProtectedRoute>}>
          {flatPaths().filter(({ path }) => !isPlatformPath(path) && path !== '/').map(({ path }) => {
            const Page = BESPOKE[path] || Stub;
            return <Route key={path} path={path.replace(/^\//, '')} element={<Page />} />;
          })}

          {/* Add / edit-airport sub-routes (not in the sidebar registry but reachable
              from the list page's "Add Airport" button and the row edit action). */}
          <Route path="airports/new" element={<AddAirport />} />
          <Route path="airports/:id/edit" element={<AddAirport />} />
          {/* Edit-category sub-route (reachable from the category list). */}
          <Route path="package/categories/:id/edit" element={<AddCategory />} />
          {/* Edit-package sub-route (reachable from the package list). */}
          <Route path="package/:id/edit" element={<AddPackage />} />
          {/* Holiday add/edit sub-routes (reachable from the holiday list). */}
          <Route path="package/holiday/new" element={<AddHoliday />} />
          <Route path="package/holiday/:id/edit" element={<AddHoliday />} />
          {/* Blog edit sub-routes. */}
          <Route path="blog/categories/:id/edit" element={<AddBlogCategory />} />
          <Route path="blog/posts/:id/edit" element={<AddBlog />} />
          {/* Flight booking documents — rendered inside the console (no redirect
              to the customer site), reachable from the Booking List actions. */}
          {/* Booking Details as a full page — the drawers' full-screen button. */}
          <Route path="flight/bookings/:id/details" element={<BookingDetailsPage product="flight" />} />
          <Route path="hotel/bookings/:id/details" element={<BookingDetailsPage product="hotel" />} />
          <Route path="insurance/bookings/:id/details" element={<BookingDetailsPage product="insurance" />} />
          <Route path="flight/bookings/:id/invoice" element={<FlightInvoiceDoc />} />
          <Route path="flight/bookings/:id/ticket" element={<FlightTicketDoc />} />
          {/* Travel-insurance document — the standalone policy's tax invoice.
              There is no voucher/e-ticket counterpart: the customer's travel
              document for this product is the insurer's own policy PDF. */}
          <Route path="insurance/bookings/:id/invoice" element={<InsuranceInvoiceDoc />} />
          {/* Hotel booking documents — admin-native (rendered in-console, no
              redirect to the customer site), reachable from the Details drawer. */}
          <Route path="hotel/bookings/:id/invoice" element={<HotelInvoiceDoc />} />
          <Route path="hotel/bookings/:id/voucher" element={<HotelVoucherDoc />} />
          {/* Hotel markup / discount edit sub-routes (reachable from their lists). */}
          <Route path="hotel/markups/:id/edit" element={<HotelAddMarkup />} />
          <Route path="hotel/discounts/:id/edit" element={<HotelAddDiscount />} />
          {/* One hotel search's full detail (reachable from the Search Log list). */}
          <Route path="hotel/search-log/:id" element={<HotelSearchLogDetails />} />
          {/* One user's wallet detail (reachable from Wallet Management). */}
          <Route path="account/wallets/:userId" element={<WalletDetail />} />
          {/* Staff edit sub-route. */}
          <Route path="staff/:id/edit" element={<AddStaff />} />
          {/* Offer edit sub-route (reachable from the offer list). */}
          <Route path="offers/:id/edit" element={<AddOffer />} />
          {/* Testimonial edit sub-route (reachable from the testimonial list). */}
          <Route path="testimonials/:id/edit" element={<AddTestimonial />} />
          {/* FAQ edit sub-route (reachable from the FAQ list). */}
          <Route path="faqs/:id/edit" element={<AddFaq />} />
          {/* One page's SEO (reachable from SEO Management → Page SEO). */}
          <Route path="seo/pages/:pageKey" element={<SeoPageEditor />} />
          </Route>

          {/* Platform routes (Tenant Management, Platform Admin Users) — super_admin
              only. A tenant_admin who navigates here directly bounces to the
              dashboard (see ProtectedRoute's role prop); the real boundary is the
              backend's authorize('super_admin') on these API routes. */}
          <Route element={<ProtectedRoute role="super_admin"><Outlet /></ProtectedRoute>}>
            {flatPaths().filter(({ path }) => isPlatformPath(path)).map(({ path }) => {
              const Page = BESPOKE[path] || Stub;
              return <Route key={path} path={path.replace(/^\//, '')} element={<Page />} />;
            })}
            {/* Not in nav.js (see its changelog) — the Tenant List's "+ Add
                Tenant" button is the only entry point. */}
            <Route path="platform/tenants/new" element={<AddTenant />} />
            <Route path="platform/tenants/:id/edit" element={<AddTenant />} />
            <Route path="platform/admin-users/:id/edit" element={<AddPlatformAdminUser />} />
          </Route>
        </Route>

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <ToastHost />
      <ConfirmHost />
    </>
  );
}
